import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { LoadingSpinner, Panel, Tag } from '@components';
import { useCompany } from '@hooks';
import { AssetsService, UsersService } from '@services';

import DefaultButton from '@components/buttons/DefaultButton';
import Icon from '@components/typography/AppIcon';
import { getPeerStatus } from '@containers';
import {
  AppDispatch,
  RootState,
  approveVpnUser,
  updateVpnUserAsync,
} from '@stores';
import { ListComponent } from '@utils';
import { NetbirdPeer } from '../../interfaces/Netbird';

interface Props {
  data: {
    userId: string;
  };
}

const vpnService = new UsersService();
const assetService = new AssetsService();

export const VpnUserDetailContainer = ({ data }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { companyId } = useCompany();
  const [loading, setLoading] = useState<boolean>(false);
  const user = useSelector((state: RootState) =>
    state.netbirdUsers.users.find((u) => u.id === data.userId)
  );

  const peers = useSelector((state: RootState) => state.netbirdPeers.peers);

  if (!user) {
    return <LoadingSpinner />;
  }

  const approveUser = async () => {
    try {
      await dispatch(
        approveVpnUser({
          userId: user.id,
          companyId: companyId || 0,
        })
      );
    } catch (err) {
      console.error('Approve failed', err);
    }
  };

  const toggleBlockUser = async () => {
    setLoading(true);
    try {
      await dispatch(
        updateVpnUserAsync({
          userId: user.id,
          companyId: companyId || 0,
          data: {
            is_blocked: user.status !== 'blocked',
            role: user.role,
            auto_groups: user.auto_groups,
          },
        })
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const userPeers = useMemo(
    () => peers.filter((peer) => peer.user_id === user.id),
    [peers, user.id]
  );

  const onFindSerial = async (serial: string) => {
    try {
      await assetService.getBySerialOnRmm(serial, companyId);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="font-semibold text-lg">{user.name}</h2>

      <div className="text-sm text-zinc-500">{user.email}</div>

      <div>
        Status:{' '}
        <strong>{user.pending_approval ? 'Pending' : user.status}</strong>
      </div>

      {user.pending_approval && (
        <button
          onClick={approveUser}
          className="px-3 py-2 rounded bg-yellow-500 text-white"
        >
          Approve
        </button>
      )}

      <DefaultButton
        onclick={toggleBlockUser}
        style={user.status === 'blocked' ? 'blue' : 'danger'}
        size={'small'}
        loading={loading}
      >
        {user.status === 'blocked' ? 'Deblokkeer' : 'Blokkeer toegang'}
      </DefaultButton>

      {userPeers && (
        <div className="grid grid-cols-2 gap-4">
          <ListComponent
            data={userPeers}
            renderItem={(peer: NetbirdPeer) => {
              const status = getPeerStatus(peer);

              return (
                <Panel>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {/* STATUS */}
                      <div className="mt-1 relative flex items-center justify-center">
                        {status.animate && (
                          <span
                            className={`absolute h-3 w-3 rounded-full ${status.color} animate-ping opacity-30`}
                          />
                        )}

                        <span
                          className={`relative h-2.5 w-2.5 rounded-full ${status.color}`}
                        />
                      </div>

                      {/* INFO */}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{peer.hostname}</span>

                          <Tag
                            label={
                              peer.connected
                                ? 'online'
                                : peer.login_expired
                                  ? 'expired'
                                  : 'offline'
                            }
                            style={
                              peer.connected
                                ? 'success'
                                : peer.login_expired
                                  ? 'danger'
                                  : 'neutral'
                            }
                            lowercase
                          />
                        </div>

                        <div className="text-xs text-zinc-500">
                          {peer.dns_label}
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                          <div>
                            <span className="text-zinc-400">Platform</span>
                            <div>{peer.os}</div>
                          </div>

                          <div>
                            <span className="text-zinc-400">Agent</span>
                            <div>v{peer.version}</div>
                          </div>

                          <div>
                            <span className="text-zinc-400">Location</span>
                            <div>
                              {peer.city_name}, {peer.country_code}
                            </div>
                          </div>

                          <div>
                            <span className="text-zinc-400">SSH</span>
                            <div>
                              {peer.ssh_enabled ? 'Enabled' : 'Disabled'}
                            </div>
                          </div>

                          <div>
                            <span className="text-zinc-400">IP</span>
                            <div>{peer.ip}</div>
                          </div>

                          <div>
                            <span className="text-zinc-400">Peers</span>
                            <div>{peer.accessible_peers_count}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* DEVICE TYPE ICON */}
                    <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-slate-100 dark:bg-slate-800">
                      <Icon name="Monitor" size="sm" />
                    </div>
                  </div>

                  {/* GROUPS */}
                  {peer.groups?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1">
                      {peer.groups.map((group) => (
                        <Tag
                          key={group.id}
                          label={group.name}
                          style="info"
                          lowercase
                        />
                      ))}
                    </div>
                  )}
                </Panel>
              );
            }}
          />
        </div>
      )}
    </div>
  );
};

export default VpnUserDetailContainer;
